import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { CallerName } from '@/components/crm/CallerName';
import { fetchCallSummary, fetchQueueSetup } from '@/services/api';
import { dispositionBucket, isQueueCall } from '@/utils/callRecords';
import { followUpOf, recordTimeMs, ringTime } from '@/lib/reports/dailyActivityReport';
import type { CallRecord, CallSummary, Extension } from '@/types';
import { callLegs, describeCall, endedByText, placeText, statusText } from './callDetail';

interface CallDetailPanelProps {
  record: CallRecord;
  /** All CDR rows of the period, for the call's other legs and any call back. */
  records: CallRecord[];
  extensions: Extension[];
  backLabel: string;
  onBack: () => void;
}

type QueueSetup = Awaited<ReturnType<typeof fetchQueueSetup>>;

const timeFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const clockFormat = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function formatWhen(ms: number, fallback: string) {
  return ms ? timeFormat.format(new Date(ms)) : fallback;
}

function queueNumber(record: CallRecord) {
  return String(record.call_to_number || record.call_to.match(/<([^>]+)>\s*$/)?.[1] || '').trim();
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

function legVariant(record: CallRecord) {
  const bucket = dispositionBucket(record);
  return bucket === 'answered' ? 'success' : bucket === 'busy' ? 'warning' : 'danger';
}

export function CallDetailPanel({ record, records, extensions, backLabel, onBack }: CallDetailPanelProps) {
  const [summary, setSummary] = useState<CallSummary | null>(null);
  const [summaryState, setSummaryState] = useState<'loading' | 'done' | 'unavailable'>('loading');
  const [queueSetup, setQueueSetup] = useState<QueueSetup>(null);
  const atMs = recordTimeMs(record);
  const queueCall = isQueueCall(record);

  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    setSummaryState('loading');
    fetchCallSummary(String(record.uid || ''), atMs)
      .then((result) => {
        if (cancelled) return;
        setSummary(result);
        setSummaryState(result ? 'done' : 'unavailable');
      })
      .catch((error: unknown) => {
        console.warn('Call summary unavailable:', error);
        if (!cancelled) setSummaryState('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [atMs, record.uid]);

  useEffect(() => {
    let cancelled = false;
    setQueueSetup(null);
    const number = queueCall ? queueNumber(record) : '';
    if (!number) return;
    fetchQueueSetup(number)
      .then((result) => {
        if (!cancelled) setQueueSetup(result);
      })
      .catch((error: unknown) => console.warn('Queue setup unavailable:', error));
    return () => {
      cancelled = true;
    };
  }, [queueCall, record]);

  const legs = useMemo(() => callLegs(record, records), [record, records]);
  const followUp = useMemo(() => {
    if (record.call_type !== 'Inbound' || dispositionBucket(record) === 'answered') return null;
    const names = new Map(extensions.map((ext) => [String(ext.number), ext.display_name || ext.username || '']));
    const label = (number: string) => (names.get(number) ? `${names.get(number)} (${number})` : number);
    return followUpOf(record, atMs, records, label, new Set(names.keys()));
  }, [atMs, extensions, record, records]);

  const loading = summaryState === 'loading';
  const headline = describeCall(record, legs, summary);
  const endedBy = endedByText(summary?.disconnectedBy, record.call_type);
  // The leg's own result, unless the call ended unanswered and v2 says more (e.g. the caller hung up)
  const badge =
    summary && summary.status !== 'ANSWERED' && dispositionBucket(record) !== 'answered'
      ? statusText(summary.status)
      : record.disposition;
  const trunks = summary?.sourceTrunks.length
    ? summary.sourceTrunks
    : summary?.destinationTrunks.length
      ? summary.destinationTrunks
      : [record.src_trunk || record.dst_trunk].filter((trunk): trunk is string => Boolean(trunk));
  const dids = summary?.dids.length ? summary.dids : [record.did_number].filter((did): did is string => Boolean(did));
  const route = [...(summary?.ivrs ?? []).map((ivr) => `IVR ${placeText(ivr)}`)];
  const showPath = legs.length > 1 || route.length > 0;

  const callBack = (() => {
    if (!followUp) return null;
    switch (followUp.kind) {
      case 'calledBack':
        return `Called back by ${followUp.by} at ${clockFormat.format(new Date(followUp.atMs))}${followUp.answered ? ' (answered)' : ' (not answered)'}`;
      case 'rangAgain':
        return `Rang again at ${clockFormat.format(new Date(followUp.atMs))} and got through to ${followUp.by}`;
      case 'unknownCaller':
        return 'No caller number to call back';
      default:
        return 'Not yet, as far as this period’s calls show';
    }
  })();

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
      >
        <ArrowLeft size={16} />
        {backLabel}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            <CallerName value={record.call_from} />
          </div>
          {summary?.callerCompany && <p className="text-sm text-gray-600 dark:text-gray-300">{summary.callerCompany}</p>}
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            to {placeText(record.call_to)} · {formatWhen(atMs, record.time)}
          </p>
        </div>
        <Badge variant={legVariant(record)}>{badge}</Badge>
      </div>

      <div className="rounded-xl bg-panel p-4 text-sm text-gray-800 dark:bg-gray-900/60 dark:text-gray-100">
        <p className="font-medium">{headline}</p>
        {loading && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Checking with the phone system for more...</p>}
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="Called">{placeText(record.call_to)}</Fact>
        <Fact label="Hung up by">{endedBy ?? (loading ? '...' : 'Not reported by this phone system')}</Fact>
        <Fact label="Rang for">{ringTime(record.ring_duration || 0)}</Fact>
        <Fact label="Talk time">{ringTime(record.talk_duration || 0)}</Fact>
        <Fact label="Whole call">
          {ringTime(summary?.callDuration ?? record.duration ?? record.ring_duration ?? 0)}
          {summary?.status === 'ANSWERED' && summary.routingDuration !== undefined && (
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {ringTime(summary.routingDuration)} before anyone answered
            </span>
          )}
        </Fact>
        {summary && <Fact label="How the call ended">{statusText(summary.status)}</Fact>}
        {trunks.length > 0 && (
          <Fact label={record.call_type === 'Outbound' ? 'Went out on' : 'Came in on'}>{trunks.join(', ')}</Fact>
        )}
        {dids.length > 0 && <Fact label="Number dialled">{dids.join(', ')}</Fact>}
        {callBack && <Fact label="Called back?">{callBack}</Fact>}
        {record.uid && (
          <Fact label="Call ID">
            <span className="font-mono text-xs">{record.uid}</span>
          </Fact>
        )}
      </dl>

      {showPath && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Call path</h4>
          {route.length > 0 && (
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              Went through {route.join(', ')}
              {summary?.queues.length
                ? `, then ${summary.queues.map((q) => `${placeText(q.name)}${q.status ? ` (${statusText(q.status).toLowerCase()})` : ''}`).join(', ')}`
                : ''}
            </p>
          )}
          <ol className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
            {legs.map((leg, index) => (
              <li
                key={leg.new_id || leg.id || index}
                className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm ${
                  leg === record ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                }`}
              >
                <span className="w-16 shrink-0 tabular-nums text-gray-500 dark:text-gray-400">
                  {recordTimeMs(leg) ? clockFormat.format(new Date(recordTimeMs(leg))) : leg.time}
                </span>
                <span className="min-w-0 flex-1 text-gray-900 dark:text-gray-100">{placeText(leg.call_to)}</span>
                <Badge variant={legVariant(leg)}>{leg.disposition}</Badge>
                <span className="w-44 shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
                  rang {ringTime(leg.ring_duration || 0)}
                  {leg.talk_duration ? ` · talked ${ringTime(leg.talk_duration)}` : ''}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {queueSetup && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
            Queue {placeText(record.call_to)} setup
          </h4>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <Fact label="Agents who would have rung">{queueSetup.agents.join(', ') || 'None listed'}</Fact>
            {queueSetup.detail.ringStrategy && <Fact label="Ring strategy">{queueSetup.detail.ringStrategy}</Fact>}
            {queueSetup.detail.maxWaitTime !== undefined && queueSetup.detail.maxWaitTime !== '' && (
              <Fact label="Longest wait before failover">{ringTime(Number(queueSetup.detail.maxWaitTime) || 0)}</Fact>
            )}
            {queueSetup.failover && queueSetup.failover !== '—' && <Fact label="Failover">{queueSetup.failover}</Fact>}
          </dl>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">This is the queue’s setup now, which may have changed since the call.</p>
        </div>
      )}
    </div>
  );
}
